import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ThumbsUp, ThumbsDown, Info, MapPin, Eye, Stethoscope, FlaskConical, Copy, CopyPlus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Sheet } from '@/components/ui/Sheet'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { AsyncSelect } from '@/components/ui/AsyncSelect'
import { Textarea } from '@/components/ui/Textarea'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ProviderViewModal, type ProviderViewMode } from './ProviderViewModal'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useAuthority } from '@/hooks/useAuthority'
import {
  CredentialingProvidersService,
  CredentialingStepsService,
  CredentialingTypesService,
  type CredentialingProvider,
  type CredentialingStep,
  type CredentialingProviderType,
} from '@/services/modules/CredentialingService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { dateTimeBR, maskCpfPartial, maskCnpjPartial } from '@/lib/format'

/** Bolinha de status do credenciamento. */
const PROVIDER_STATUS_DOT: Record<string, { color: string; label: string }> = {
  ACEITO:         { color: 'bg-emerald-500', label: 'Aceito' },
  REJEITADO:      { color: 'bg-rose-500',    label: 'Rejeitado' },
  'EM ANDAMENTO': { color: 'bg-amber-400',   label: 'Em andamento' },
}

function ProviderStatusDot({ status }: { status: string }) {
  const cfg = PROVIDER_STATUS_DOT[status] ?? { color: 'bg-gray-300', label: status }
  return (
    <span className="inline-flex items-center justify-center" title={cfg.label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.color}`} />
    </span>
  )
}

/** Resume PESSOA FÍSICA → PF, PESSOA JURÍDICA → PJ. */
function shortType(type: string | null | undefined): string {
  const t = (type ?? '').toUpperCase()
  if (t.includes('FÍSICA') || t.includes('FISICA')) return 'PF'
  if (t.includes('JURÍDICA') || t.includes('JURIDICA')) return 'PJ'
  return type ?? '—'
}

function categoryPath(p: CredentialingProvider): string {
  return p.providerTypePath ?? p.providerType?.path ?? p.providerType?.type ?? '—'
}

interface Filters {
  name: string
  document: string
  documentKind: 'CPF' | 'CNPJ'
  typeId: string
  stepId: string
  parentId: string
  /** Rótulo do contrato principal selecionado (para reexibir sem recarregar). */
  parentLabel: string
  stateId: string
  cityId: string
  status: string
}

const EMPTY_FILTERS: Filters = {
  name: '', document: '', documentKind: 'CNPJ',
  typeId: '', stepId: '', parentId: '', parentLabel: '', stateId: '', cityId: '', status: 'ACEITO',
}

/** Status do credenciamento — botões 2 por linha (Aceito é o padrão). */
const STATUS_BUTTONS = [
  { value: '', label: 'Todos' },
  { value: 'ACEITO', label: 'Aceito' },
  { value: 'EM ANDAMENTO', label: 'Em andamento' },
  { value: 'REJEITADO', label: 'Rejeitado' },
]

export function CredentialingProvidersPage() {
  const nav = useNavigate()
  const [reload, setReload] = useState(0)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Mapeia os filtros locais para os query params do backend (->filter()).
  const apiFilters = useMemo(() => {
    const q: Record<string, unknown> = {}
    if (filters.name) q['name[like]'] = filters.name
    if (filters.document) q['document[like]'] = filters.document.replace(/\D+/g, '')
    if (filters.typeId) q.type_id = filters.typeId
    if (filters.stepId) q.step_id = filters.stepId
    if (filters.parentId) q.parent_id = filters.parentId
    if (filters.cityId) q.city_id = filters.cityId
    if (filters.status) q.status = filters.status
    // Estado: filtra via relação city.state_id.
    if (filters.stateId && !filters.cityId) {
      q['relation_has[0][relation]'] = 'city'
      q['relation_has[0][field]'] = 'state_id'
      q['relation_has[0][value]'] = filters.stateId
    }
    return q
  }, [filters])

  const activeCount = useMemo(
    () => [filters.name, filters.document, filters.typeId, filters.stepId, filters.parentId, filters.stateId, filters.cityId, filters.status].filter(Boolean).length,
    [filters],
  )

  const { setPage, size, setSize, rows, meta, loading } = useCrudList(
    CredentialingProvidersService,
    { size: 25, reloadKey: reload, filters: apiFilters },
  )

  // Exportação Excel — mesmas colunas da linha (nome/documento/categoria desdobrados).
  const exp = useDataTableExport({
    filename: 'credenciados',
    service: CredentialingProvidersService,
    filters: apiFilters,
    columns: [
      { header: 'Credenciado',        accessor: (p: CredentialingProvider) => p.name || '' },
      { header: 'Documento',          accessor: (p: CredentialingProvider) => p.formatted?.document ?? p.document ?? '' },
      { header: 'Categoria',          accessor: (p: CredentialingProvider) => categoryPath(p) },
      { header: 'Contrato principal', accessor: (p: CredentialingProvider) => p.parent?.name ?? '' },
      { header: 'Tipo',               accessor: (p: CredentialingProvider) => shortType(p.type) },
      { header: 'Cidade',             accessor: (p: CredentialingProvider) => p.address?.city?.city ?? '' },
      { header: 'UF',                 accessor: (p: CredentialingProvider) => p.address?.city?.state?.abbreviation ?? '' },
      { header: 'Especialidades',     accessor: (p: CredentialingProvider) => p.specialtiesCount ?? p.specialties?.length ?? 0 },
      { header: 'Exames',             accessor: (p: CredentialingProvider) => p.testsCount ?? p.tests?.length ?? 0 },
      { header: 'Status',             accessor: (p: CredentialingProvider) => PROVIDER_STATUS_DOT[p.status]?.label ?? p.status },
      { header: 'Cadastrado em',      accessor: (p: CredentialingProvider) => p.formatted?.createdAt ?? dateTimeBR((p as { createdAt?: string }).createdAt) },
    ],
  })

  const [rejecting, setRejecting] = useState<CredentialingProvider | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectSaving, setRejectSaving] = useState(false)
  const [toDelete, setToDelete] = useState<CredentialingProvider | null>(null)
  const [copyTarget, setCopyTarget] = useState<{ p: CredentialingProvider; kind: 'specialties' | 'tests' } | null>(null)
  const [view, setView] = useState<{ id: string; name: string; mode: ProviderViewMode } | null>(null)
  const [busy, setBusy] = useState(false)
  const { can } = useAuthority()
  const canCreate = can('credentialing.providers.create')
  const canEdit = can('credentialing.providers.edit')
  const canDelete = can('credentialing.providers.delete')
  const canApprove = can('credentialing.providers.approve')
  const canReject = can('credentialing.providers.reject')
  const canManageSpecialties = can('credentialing.providers.manage_specialties')
  const canManageTests = can('credentialing.providers.manage_tests')

  function refresh() { setReload(v => v + 1) }

  async function approve(p: CredentialingProvider) {
    try { await CredentialingProvidersService.approve(p.id); toast.success('Credenciado aprovado.'); refresh() }
    catch { toast.error('Erro ao aprovar.') }
  }
  async function confirmReject() {
    if (!rejecting) return
    if (rejectReason.trim().length < 3) {
      toast.error({ title: 'Justificativa obrigatória', description: 'Descreva o motivo da rejeição.' })
      return
    }
    setRejectSaving(true)
    try {
      await CredentialingProvidersService.reject(rejecting.id, rejectReason.trim())
      toast.success('Credenciado rejeitado.')
      setRejecting(null); refresh()
    } catch { toast.error('Erro ao rejeitar.') }
    finally { setRejectSaving(false) }
  }
  async function confirmCopy() {
    if (!copyTarget) return
    setBusy(true)
    try {
      if (copyTarget.kind === 'specialties') await CredentialingProvidersService.copySpecialties(copyTarget.p.id)
      else await CredentialingProvidersService.copyTests(copyTarget.p.id)
      toast.success({ title: 'Copiado', description: copyTarget.kind === 'specialties' ? 'Especialidades copiadas do contrato principal.' : 'Exames copiados do contrato principal.' })
      setCopyTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível copiar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }
  async function confirmDelete() {
    if (!toDelete) return
    setBusy(true)
    try {
      await CredentialingProvidersService.destroy(toDelete.id)
      toast.success('Credenciado removido.')
      setToDelete(null); refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const columns: DataTableColumn<CredentialingProvider>[] = [
    // 01 — Info (esquerda) + nome/documento/categoria na mesma coluna.
    //      A coluna ocupa o espaço livre (w-full) para a tabela usar 100%.
    { key: 'name', header: 'Credenciado', alwaysVisible: true, sortable: true, className: 'w-full', render: p => (
      <div className="flex items-center gap-3">
        <InfoPopover
          align="left"
          ariaLabel="Detalhes do credenciado"
          trigger={<Info size={16} className="text-gray-400 hover:text-brand-500 transition" />}
          title="Detalhes"
        >
          <dl className="space-y-2 text-xs">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Cadastrado em</dt>
              <dd className="font-medium text-gray-800">{p.formatted?.createdAt ?? dateTimeBR((p as { createdAt?: string }).createdAt)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Especialidades</dt>
              <dd className="font-medium text-gray-800">{p.specialtiesCount ?? p.specialties?.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Exames</dt>
              <dd className="font-medium text-gray-800">{p.testsCount ?? p.tests?.length ?? 0}</dd>
            </div>
          </dl>
        </InfoPopover>
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-medium text-gray-800">{p.name || '—'}</span>
          <span className="text-[11px] text-gray-500">{p.formatted?.document ?? p.document ?? '—'}</span>
          <span className="text-[11px] text-gray-400">{categoryPath(p)}</span>
        </div>
      </div>
    ) },
    // 02 — Contrato principal (esquerda, sem ícone).
    { key: 'contract', header: 'Contrato principal', className: 'w-48 whitespace-nowrap',
      render: p => p.parent ? <span className="text-gray-700">{p.parent.name}</span> : <span className="text-gray-400">—</span> },
    // Tipo PF/PJ.
    { key: 'type', header: 'Tipo', align: 'center', className: 'w-16', render: p => (
      <span className="text-xs font-medium text-gray-600">{shortType(p.type)}</span>
    ) },
    // Cidade — não quebra linha.
    { key: 'city', header: 'Cidade', align: 'center', className: 'w-44 whitespace-nowrap', render: p => {
      const c = p.address?.city
      return c
        ? <span className="inline-flex items-center gap-1.5 text-gray-700"><MapPin size={13} className="text-brand-500 shrink-0" />{c.city}{c.state ? `/${c.state.abbreviation}` : ''}</span>
        : <span className="text-gray-400">—</span>
    } },
    // Status (bolinha).
    { key: 'status', header: 'Status', align: 'center', className: 'w-20', render: p => <ProviderStatusDot status={p.status} /> },
  ]

  function rowActions(p: CredentialingProvider): DropdownItem[] {
    const items: DropdownItem[] = [
      // A tela do credenciado também é a de consulta: quem não edita entra só para ver.
      { key: 'edit', label: canEdit ? 'Ver e editar' : 'Ver', icon: <Eye size={14} />, onClick: () => nav(`/credentialing/providers/${p.id}/editar`) },
      { key: 'specialties', label: 'Ver especialidades', icon: <Stethoscope size={14} />, onClick: () => setView({ id: p.id, name: p.name, mode: 'specialties' }) },
      { key: 'tests', label: 'Ver exames', icon: <FlaskConical size={14} />, onClick: () => setView({ id: p.id, name: p.name, mode: 'tests' }) },
    ]
    if (p.parent) {
      const copy: DropdownItem[] = []
      if (canManageSpecialties) copy.push({ key: 'copy-spec', label: 'Copiar especialidades', icon: <Copy size={14} />, onClick: () => setCopyTarget({ p, kind: 'specialties' }) })
      if (canManageTests) copy.push({ key: 'copy-tests', label: 'Copiar exames', icon: <CopyPlus size={14} />, onClick: () => setCopyTarget({ p, kind: 'tests' }) })
      if (copy.length) copy[0] = { ...copy[0], divider: true }
      items.push(...copy)
    }
    if (p.status === 'EM ANDAMENTO') {
      const decide: DropdownItem[] = []
      if (canApprove) decide.push({ key: 'approve', label: 'Aprovar', icon: <ThumbsUp size={14} />, onClick: () => approve(p) })
      if (canReject) decide.push({ key: 'reject', label: 'Rejeitar', icon: <ThumbsDown size={14} />, onClick: () => { setRejecting(p); setRejectReason('') } })
      if (decide.length) decide[0] = { ...decide[0], divider: true }
      items.push(...decide)
    }
    if (canDelete) items.push({ key: 'delete', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setToDelete(p) })
    return items
  }

  return (
    <>
      <PageHeader title="Credenciados" subtitle="Médicos, clínicas, laboratórios e dentistas"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Credenciados' }]}
        actions={canCreate ? <Button onClick={() => nav('/credentialing/providers/novo')} iconLeft={<Plus size={14} />}>Novo credenciado</Button> : undefined}
      />
      <Toolbar onFiltersClick={() => setDrawerOpen(true)} activeFiltersCount={activeCount} />
      <DataTable
        columns={columns} rows={rows} loading={loading} getRowKey={r => r.id} rowActions={rowActions}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ProviderFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        values={filters}
        onApply={(v) => {
          // Busca por documento é um credenciado específico, não uma navegação:
          // manter o filtro padrão "Aceito" escondia quem estava EM ANDAMENTO e
          // fazia a busca parecer que ninguém usava aquele CNPJ.
          const ajustado = v.document && v.status === 'ACEITO' ? { ...v, status: '' } : v
          setFilters(ajustado); setPage(1); setDrawerOpen(false)
        }}
        onClear={() => { setFilters(EMPTY_FILTERS); setPage(1); setDrawerOpen(false) }}
      />

      {/* Ver especialidades / exames — mesma modal do kanban */}
      {view && (
        <ProviderViewModal
          providerId={view.id}
          providerName={view.name}
          mode={view.mode}
          onClose={() => setView(null)}
          onChanged={refresh}
        />
      )}

      {/* Rejeitar */}
      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={`Rejeitar ${rejecting?.name ?? 'credenciado'}`}
        footer={<>
          <Button variant="ghost" onClick={() => setRejecting(null)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmReject} loading={rejectSaving}>Rejeitar</Button>
        </>}
      >
        <p className="text-sm text-gray-600 mb-3">Informe a justificativa da rejeição. Ela ficará registrada no credenciado.</p>
        <Textarea label="Justificativa" required value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="Ex.: documentação incompleta" />
      </Modal>

      {/* Copiar especialidades/exames */}
      <ConfirmDialog
        open={!!copyTarget}
        onClose={() => setCopyTarget(null)}
        onConfirm={confirmCopy}
        loading={busy}
        title={copyTarget?.kind === 'specialties' ? 'Copiar especialidades?' : 'Copiar exames?'}
        description={`Copiar ${copyTarget?.kind === 'specialties' ? 'as especialidades' : 'os exames'} do contrato principal "${copyTarget?.p.parent?.name ?? ''}" para "${copyTarget?.p.name ?? ''}". Itens já existentes são mantidos.`}
        confirmLabel="Copiar"
      />

      {/* Excluir */}
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        loading={busy}
        variant="danger"
        title="Excluir credenciado?"
        description={`Tem certeza que deseja remover "${toDelete?.name ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
      />
    </>
  )
}

// ---------------------------------------------------------------------------

interface DrawerProps {
  open: boolean
  onClose: () => void
  values: Filters
  onApply: (v: Filters) => void
  onClear: () => void
}

function ProviderFilterDrawer({ open, onClose, values, onApply, onClear }: DrawerProps) {
  const [local, setLocal] = useState<Filters>(values)
  const [types, setTypes] = useState<CredentialingProviderType[]>([])
  const [steps, setSteps] = useState<CredentialingStep[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])

  useEffect(() => { setLocal(values) }, [values, open])

  // Carrega lookups na primeira abertura. Categorias e etapas: apenas ATIVAS.
  useEffect(() => {
    if (!open) return
    if (types.length === 0) CredentialingTypesService.list({ size: 0, status: 'ATIVO' } as never).then(r => setTypes(r.data)).catch(() => {})
    if (steps.length === 0) CredentialingStepsService.list({ size: 0, status: 'ATIVO' } as never).then(r => setSteps(r.data)).catch(() => {})
    if (states.length === 0) LookupService.states().then(setStates).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Carrega cidades ao mudar o estado selecionado.
  useEffect(() => {
    if (!local.stateId) { setCities([]); return }
    LookupService.cities(local.stateId).then(setCities).catch(() => setCities([]))
  }, [local.stateId])

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setLocal((p) => ({ ...p, [k]: v }))

  const typeOptions = useMemo(
    () => types.map(t => ({ value: t.id, label: t.path || t.type })).sort((a, b) => a.label.localeCompare(b.label)),
    [types],
  )

  // Busca server-side de contratos principais (lista grande → não baixa tudo).
  const loadContracts = (q: string) =>
    CredentialingProvidersService.mainContractOptions(q || undefined)
      .then(list => list.map(o => ({ value: o.id, label: o.name })))

  // Máscara progressiva do documento conforme o tipo selecionado.
  const docMasked = local.documentKind === 'CNPJ' ? maskCnpjPartial(local.document) : maskCpfPartial(local.document)

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Filtros"
      size="md"
      footer={<>
        <Button variant="ghost" onClick={onClear}>Limpar</Button>
        <Button onClick={() => onApply(local)}>Aplicar</Button>
      </>}
    >
      <div className="space-y-5">
        <Input label="Nome do credenciado" placeholder="Buscar por nome" value={local.name} onChange={(e) => set('name', e.target.value)} />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Documento</label>
          <div className="inline-flex overflow-hidden rounded-md ring-1 ring-gray-200">
            {(['CNPJ', 'CPF'] as const).map(k => (
              <button
                key={k}
                type="button"
                onClick={() => set('documentKind', k)}
                className={`w-16 cursor-pointer px-2 py-1.5 text-center text-xs transition ${local.documentKind === k ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {k}
              </button>
            ))}
          </div>
          <Input
            wrapperClassName="mt-2"
            value={docMasked}
            onChange={(e) => set('document', e.target.value.replace(/\D+/g, ''))}
          />
        </div>

        <Select label="Categoria" value={local.typeId} onChange={(e) => set('typeId', e.target.value)} options={typeOptions} placeholder="Todas" />
        <Select label="Etapa de credenciamento" value={local.stepId} onChange={(e) => set('stepId', e.target.value)} options={steps.map(s => ({ value: s.id, label: s.step }))} placeholder="Todas" />

        <AsyncSelect
          label="Contrato principal"
          value={local.parentId}
          selectedLabel={local.parentLabel}
          loadOptions={loadContracts}
          onChange={(v, opt) => setLocal(p => ({ ...p, parentId: v, parentLabel: opt?.label ?? '' }))}
          placeholder="Todos"
          searchPlaceholder="Buscar por nome ou documento"
        />

        <Select
          label="Estado"
          value={local.stateId}
          onChange={(e) => setLocal(p => ({ ...p, stateId: e.target.value, cityId: '' }))}
          options={states.map(s => ({ value: s.id, label: `${s.state} (${s.abbreviation})` }))}
          placeholder="Todos"
        />
        <Select
          label="Cidade"
          value={local.cityId}
          onChange={(e) => set('cityId', e.target.value)}
          options={cities.map(c => ({ value: c.id, label: c.city }))}
          placeholder={local.stateId ? 'Todas' : 'Selecione o estado'}
          disabled={!local.stateId}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_BUTTONS.map(s => (
              <button
                key={s.value || 'all'}
                type="button"
                onClick={() => set('status', s.value)}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm ring-1 transition ${
                  local.status === s.value
                    ? 'bg-brand-500 text-white ring-brand-500'
                    : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  )
}
